import { MessageSquare, Pencil, Trash2 } from 'lucide-react';
import moment from 'moment';
import { useState } from 'react';
import Button from 'shared/components/Button';
import DeleteModal from 'shared/components/DeleteModal';
import { PlusButton } from 'shared/components/IconButtons';
import Modal from 'shared/components/modal/Modal';
import ExpandableTextCell from 'shared/components/Table/ExpandableTextCell';
import { inputClass } from 'shared/theme/colorTokens';
import { EventObservationModel } from '../../models/Evento';

interface Props {
  observations: EventObservationModel[];
  isSaving: boolean;
  isUpdating: boolean;
  isRemoving: boolean;
  isActive: boolean;
  onAdd: (observation: string) => Promise<void>;
  onUpdate: (id: number, observation: string) => Promise<void>;
  onRemove: (id: number) => Promise<void>;
}

const tooLong = (value: string) => new Blob([value.trim()]).size > 65535;
const limitMessage =
  'La observación no puede superar los 65.535 bytes en UTF-8.';

const EventoObservationsSection = ({
  observations,
  isSaving,
  isUpdating,
  isRemoving,
  isActive,
  onAdd,
  onUpdate,
  onRemove
}: Props) => {
  const [text, setText] = useState('');
  const [editing, setEditing] = useState<EventObservationModel | null>(null);
  const [editText, setEditText] = useState('');
  const [deleting, setDeleting] = useState<EventObservationModel | null>(null);
  const sorted = [...observations].sort((a, b) =>
    (b.createdAt ?? '').localeCompare(a.createdAt ?? '')
  );

  const handleAdd = async () => {
    if (!text.trim() || tooLong(text)) return;
    try {
      await onAdd(text.trim());
      setText('');
    } catch (_) {
      // El manejador de la API muestra el error; se conserva el texto.
    }
  };

  const handleUpdate = async () => {
    if (!editing || !editText.trim() || tooLong(editText)) return;
    try {
      await onUpdate(editing.id, editText.trim());
      setEditing(null);
    } catch (_) {
      // Se conserva el texto para permitir reintentar.
    }
  };

  const handleRemove = async () => {
    if (!deleting) return;
    try {
      await onRemove(deleting.id);
      setDeleting(null);
    } catch (_) {
      // El manejador de la API muestra el error.
    }
  };

  return (
    <div className="min-w-0 rounded-2xl border border-white bg-whiteLight p-6 dark:border-white/10 dark:bg-darkLight">
      <div className="mb-4 flex items-center gap-2">
        <MessageSquare size={18} className="text-secondary-500" />
        <h2 className="text-lg font-semibold text-ink dark:text-ink-dark">
          Observaciones
        </h2>
        <span className="rounded-full bg-secondary-50 px-2.5 py-0.5 text-xs font-medium text-secondary-700 dark:bg-white/10 dark:text-secondary-200">
          {observations.length}
        </span>
      </div>
      <div className="mb-5">
        <div className="flex gap-2">
          <textarea
            value={text}
            onChange={(event) => setText(event.target.value)}
            rows={2}
            aria-label="Agregar una observación"
            placeholder="Agregar una observación..."
            className={inputClass}
          />
          <PlusButton
            size={24}
            disabled={!text.trim() || tooLong(text) || isSaving}
            onClick={handleAdd}
          />
        </div>
        {tooLong(text) && (
          <p role="alert" className="mt-1 text-sm text-red-500">
            {limitMessage}
          </p>
        )}
      </div>
      <div className="flex max-h-40 flex-col gap-4 overflow-y-auto pr-1">
        {sorted.length === 0 && (
          <p className="text-sm text-inkMuted dark:text-inkMuted-dark">
            Todavía no hay observaciones.
          </p>
        )}
        {sorted.map((observation) => (
          <div
            key={observation.id}
            className="flex justify-between gap-3 border-l-2 border-secondary-200 pl-3 dark:border-white/10"
          >
            <div className="min-w-0 break-words">
              <ExpandableTextCell
                text={observation.observation}
                maxWidthClass="w-full"
              />
              <p className="text-xs text-inkMuted dark:text-inkMuted-dark">
                {observation.user?.name ?? 'Usuario'} · Última modificación:{' '}
                {observation.updatedAt
                  ? moment(observation.updatedAt).format('DD MMM YYYY HH:mm')
                  : 'Fecha no informada'}
              </p>
            </div>
            {isActive && (
              <div className="flex shrink-0 items-start gap-3">
                <button
                  type="button"
                  aria-label="Editar observación"
                  onClick={() => {
                    setEditing(observation);
                    setEditText(observation.observation);
                  }}
                  className="text-inkMuted hover:text-secondary-600 dark:text-inkMuted-dark"
                >
                  <Pencil size={15} />
                </button>
                <button
                  type="button"
                  aria-label="Eliminar observación"
                  onClick={() => setDeleting(observation)}
                  className="text-inkMuted hover:text-red-500 dark:text-inkMuted-dark"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
      <Modal
        open={editing !== null}
        onClose={() => {
          if (!isUpdating) setEditing(null);
        }}
        title="Editar observación"
        footer={
          <>
            <Button
              variant="secondary"
              size="sm"
              disabled={isUpdating}
              onClick={() => setEditing(null)}
            >
              Cancelar
            </Button>
            <Button
              size="sm"
              isLoading={isUpdating}
              disabled={!editText.trim() || tooLong(editText)}
              onClick={handleUpdate}
            >
              Guardar
            </Button>
          </>
        }
      >
        <textarea
          aria-label="Texto de la observación"
          value={editText}
          onChange={(event) => setEditText(event.target.value)}
          rows={4}
          className={inputClass}
        />
        {tooLong(editText) && (
          <p role="alert" className="text-sm text-red-500">
            {limitMessage}
          </p>
        )}
      </Modal>
      <DeleteModal
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        title="¿Eliminar esta observación?"
        body="La observación dejará de aparecer en el evento."
        onRemove={handleRemove}
        isLoading={isRemoving}
      />
    </div>
  );
};

export default EventoObservationsSection;
